import { Link, useLoaderData } from "react-router";
import type { Route } from "./+types/videos.$slug";
import { Header } from "../components/layout/Header";
import { Card } from "../components/ui/Card";
import { PUBLIC_NAV_LINKS, AUTH_NAV_LINKS } from "../components/layout/navLinks";
import { getUserFromRequest, fetchAuthMe } from "../auth.server";
import { headerUser } from "../auth";
import { breadcrumbLd, faqLd, videoLd, seoTitle } from "../utils/seo";
import { VIDEOS, getVideo, videoContentUrl, videoDuracionIso, videoDuracionTexto, videoPoster, videoSrc } from "../data/videos";

const BASE_URL = "https://www.escuelavial.cl";

export async function loader({ request, params }: Route.LoaderArgs) {
  const video = getVideo(params.slug ?? "");
  if (!video) {
    throw new Response("Video no encontrado", { status: 404 });
  }
  const user = getUserFromRequest(request);
  if (!user) return { video, user: null };
  const freshUser = await fetchAuthMe(user.token, request);
  return { video, user: headerUser(freshUser ?? user) };
}

export function meta({ data }: Route.MetaArgs) {
  const video = data?.video;
  if (!video) {
    return [{ title: "Video no encontrado | Escuela Vial" }];
  }
  const url = `${BASE_URL}/videos/${video.slug}`;
  const conVideo = `${video.titulo} (video)`;
  const title = seoTitle(conVideo.length <= 70 ? conVideo : video.titulo);
  return [
    { title },
    { name: "description", content: video.descripcion },
    { property: "og:title", content: title },
    { property: "og:description", content: video.descripcion },
    { property: "og:image", content: videoPoster(video) },
    { property: "og:url", content: url },
    { property: "og:type", content: "video.other" },
    { property: "og:video", content: videoContentUrl(video) },
    { property: "og:video:type", content: "video/mp4" },
    { property: "og:video:width", content: "1920" },
    { property: "og:video:height", content: "1080" },
    { property: "og:site_name", content: "Escuela Vial" },
    { name: "twitter:card", content: "summary_large_image" },
    { tagName: "link", rel: "canonical", href: url },
    breadcrumbLd([
      { name: "Inicio", path: "/" },
      { name: "Videos", path: "/videos" },
      { name: video.titulo, path: `/videos/${video.slug}` },
    ]),
    videoLd({
      name: video.titulo,
      description: video.descripcion,
      thumbnailUrl: videoPoster(video),
      contentUrl: videoContentUrl(video),
      uploadDate: video.fecha,
      duration: videoDuracionIso(video),
      transcript: video.transcripcion.join(" "),
      url,
    }),
    faqLd([{ q: video.pregunta.q, a: video.pregunta.a }]),
  ];
}

const CHEVRON = (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
);

export default function VideoDetalle() {
  const { video, user } = useLoaderData<typeof loader>();
  const otros = VIDEOS.filter((v) => v.slug !== video.slug).slice(0, 3);
  return (
    <div className="min-h-screen bg-[#F7F7F7]">
      <Header links={user ? AUTH_NAV_LINKS : PUBLIC_NAV_LINKS} user={user ?? undefined} />

      <main className="mx-auto max-w-3xl px-4 py-12 sm:py-16">
        {/* Breadcrumb */}
        <nav className="mb-6 flex flex-wrap items-center gap-2 text-sm text-gray-500">
          <Link to="/videos" className="hover:text-gray-900 transition-colors">Videos</Link>
          {CHEVRON}
          <span className="font-medium text-gray-900">{video.capitulo.nombre}</span>
        </nav>

        <header className="mb-6">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-900 px-3 py-1 text-xs font-semibold text-white">
            Video · {videoDuracionTexto(video)}
          </span>
          <h1 className="mt-3 text-3xl font-extrabold text-gray-900 sm:text-4xl">{video.titulo}</h1>
          <p className="mt-3 text-gray-600">{video.descripcion}</p>
        </header>

        {/* Sin botón de descarga ni "Guardar video como" del clic derecho (la opción de proyectar se mantiene). */}
        <video
          controls
          controlsList="nodownload"
          onContextMenu={(e) => e.preventDefault()}
          playsInline
          preload="metadata"
          poster={videoPoster(video)}
          width={1920}
          height={1080}
          className="aspect-video w-full rounded-2xl bg-gray-900 shadow-sm"
        >
          <source src={videoSrc(video)} type="video/mp4" />
          Tu navegador no admite la reproducción de video.
        </video>

        <div className="mt-10 space-y-8">
          <section>
            <h2 className="mb-3 text-xl font-bold text-gray-900">Transcripción</h2>
            <div className="space-y-2">
              {video.transcripcion.map((p, i) => (
                <p key={i} className="text-gray-600 leading-relaxed">{p}</p>
              ))}
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-gray-900">La pregunta del examen</h2>
            <Card className="p-5">
              <p className="text-xs font-semibold uppercase tracking-wider text-rose-600">
                El {video.pregunta.error} % la responde mal en Escuela Vial
              </p>
              <h3 className="mt-2 font-semibold text-gray-900">{video.pregunta.q}</h3>
              <p className="mt-2 text-sm text-gray-600 leading-relaxed">{video.pregunta.a}</p>
            </Card>
          </section>
        </div>

        {/* CTA */}
        <section className="mt-14">
          <div className="rounded-2xl bg-rose-600 px-8 py-12 text-center text-white">
            <h2 className="text-2xl font-bold">¿La habrías respondido bien?</h2>
            <p className="mx-auto mt-3 max-w-lg text-sm text-rose-100">
              Practica con preguntas basadas en el Libro del Conductor de CONASET y llega preparado al examen teórico.
            </p>
            <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <Link to={user ? "/practicar" : "/test-gratis"} className="inline-flex items-center justify-center rounded-lg bg-white px-6 py-3 font-semibold text-rose-600 hover:bg-rose-50 transition-colors">
                {user ? "Practicar ahora" : "Hacer un test gratis"}
              </Link>
              <Link to={video.capitulo.ruta} className="inline-flex items-center justify-center rounded-lg border border-white px-6 py-3 font-semibold text-white hover:bg-rose-700 transition-colors">
                Leer el capítulo
              </Link>
            </div>
          </div>
        </section>

        {/* Fuente */}
        <p className="mt-10 text-center text-xs text-gray-400">
          Fuente: Libro para la Conducción en Chile (Clase B), CONASET — capítulo «{video.capitulo.nombre}».
        </p>

        {/* Más videos */}
        <div className="mt-10 border-t border-gray-200 pt-8">
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-gray-500">Más videos</h2>
          <div className="grid gap-4 sm:grid-cols-3">
            {otros.map((v) => (
              <Link key={v.slug} to={`/videos/${v.slug}`} className="group overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm transition-shadow hover:shadow-md">
                <img src={videoPoster(v, 480)} alt={v.titulo} width={480} height={270} loading="lazy" className="aspect-video w-full object-cover" />
                <p className="p-3 text-sm font-semibold text-gray-900 group-hover:text-rose-700">{v.titulo}</p>
              </Link>
            ))}
          </div>
          <Link to="/videos" className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-rose-600 hover:text-rose-700">
            Ver todos los videos {CHEVRON}
          </Link>
        </div>
      </main>
    </div>
  );
}
